import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ProfileCard } from '../components/ProfileCard';
import { SuggestionList } from '../components/SuggestionList';
import { CampaignManager } from '../components/dashboard/CampaignManager';
import { Button } from '../components/ui/button';
import { useAppStore } from '../store/appStore';

export const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const { currentAccount, refreshSuggestions, subscribeToSuggestions, unsubscribeFromSuggestions, logout, setHasFollowed, updateAccount } = useAppStore();
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [fullscreen, setFullscreen] = useState(false);
  const [isToggling, setIsToggling] = useState(false);

  // closed = closesAt is set (any value means manually closed)
  const isClosed = !!currentAccount?.closesAt;

  useEffect(() => {
    if (currentAccount) {
      refreshSuggestions();
      subscribeToSuggestions();
    }
    return () => {
      unsubscribeFromSuggestions();
    };
  }, [currentAccount?.id]);

  const handleRefresh = async () => {
    setIsRefreshing(true);
    try {
      await refreshSuggestions();
    } finally {
      setIsRefreshing(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/signin');
  };

  const handleTestRequest = () => {
    if (!currentAccount) return;
    setHasFollowed(false);
    window.open(`/request/${currentAccount.handle}`, '_blank');
  };

  const handleOpen = async () => {
    if (!currentAccount) return;
    setIsToggling(true);
    try {
      await updateAccount(currentAccount.id, { closesAt: null });
    } catch (err: any) {
      console.error('Failed to open:', err);
      alert('Kunne ikke åpne: ' + (err?.message || 'Ukjent feil'));
    } finally {
      setIsToggling(false);
    }
  };

  const handleClose = async () => {
    if (!currentAccount) return;
    setIsToggling(true);
    try {
      await updateAccount(currentAccount.id, { closesAt: new Date().toISOString() });
    } catch (err: any) {
      console.error('Failed to close:', err);
      alert('Kunne ikke stenge: ' + (err?.message || 'Ukjent feil'));
    } finally {
      setIsToggling(false);
    }
  };

  if (!currentAccount) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center p-6">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-white mb-4">Ingen konto funnet</h2>
          <Button onClick={() => navigate('/signin')} className="bg-white text-black hover:bg-gray-200">
            Logg inn
          </Button>
        </div>
      </div>
    );
  }

  if (fullscreen) {
    return (
      <div className="fixed inset-0 z-50 bg-[#0a0a0a] flex flex-col">
        <div className="flex items-center justify-between px-4 sm:px-8 py-4 border-b border-white/10">
          <h1 className="text-2xl font-bold text-white">{currentAccount.displayName}</h1>
          <div className="flex items-center gap-3">
            <div className={`w-3 h-3 rounded-full ${isClosed ? 'bg-red-500' : 'bg-green-500 animate-pulse'}`} />
            <span className={`text-sm font-semibold ${isClosed ? 'text-red-400' : 'text-green-400'}`}>
              {isClosed ? 'STENGT' : 'ÅPEN'}
            </span>
            <Button
              onClick={() => setFullscreen(false)}
              className="bg-white/10 hover:bg-white/20 text-white ml-4"
            >
              Lukk fullskjerm
            </Button>
          </div>
        </div>
        <div className="flex-1 overflow-hidden">
          <SuggestionList fullscreen />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0a0a] p-4 sm:p-6 pb-16 sm:pb-24">
      <div className="max-w-2xl mx-auto space-y-4 sm:space-y-6">
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-bold text-white">Dashboard</h1>
          <div className="flex gap-2 items-center">
            <Button
              onClick={handleRefresh}
              className="bg-white/10 hover:bg-white/20 text-white px-4 py-2 rounded-lg text-sm"
              disabled={isRefreshing}
            >
              {isRefreshing ? 'Oppdaterer...' : 'Oppdater'}
            </Button>
            <span className="text-white/60 font-medium text-sm">Scenechat</span>
          </div>
        </div>

        {/* Status banner + toggle */}
        <div className={`rounded-xl p-5 border-2 ${isClosed ? 'bg-red-500/10 border-red-500/30' : 'bg-green-500/10 border-green-500/30'}`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className={`w-3 h-3 rounded-full ${isClosed ? 'bg-red-500' : 'bg-green-500 animate-pulse'}`} />
              <h3 className={`text-lg font-bold ${isClosed ? 'text-red-400' : 'text-green-400'}`}>
                {isClosed ? 'Ønsker er STENGT' : 'Ønsker er ÅPNE'}
              </h3>
            </div>
            {isClosed ? (
              <Button
                onClick={handleOpen}
                disabled={isToggling}
                className="bg-green-600 hover:bg-green-700 text-white font-semibold px-4 py-2 sm:px-8 sm:py-3"
              >
                {isToggling ? '...' : 'Åpne'}
              </Button>
            ) : (
              <Button
                onClick={handleClose}
                disabled={isToggling}
                className="bg-red-600 hover:bg-red-700 text-white font-semibold px-4 py-2 sm:px-8 sm:py-3"
              >
                {isToggling ? '...' : 'Steng'}
              </Button>
            )}
          </div>
        </div>

        <ProfileCard />

        <div className="flex gap-2">
          <Button
            onClick={() => setFullscreen(true)}
            className="flex-1 bg-white/10 hover:bg-white/20 text-white py-3"
          >
            ⛶ Fullskjerm
          </Button>
          <Button
            onClick={handleTestRequest}
            className="flex-1 bg-white/10 hover:bg-white/20 text-white py-3"
          >
            Test forslagsskjema
          </Button>
        </div>

        <SuggestionList />

        {/* Campaign manager — shown for accounts with campaigns (e.g. demokratiuka) */}
        {currentAccount.theme && (
          <div className="bg-[#1a1a1a] rounded-xl p-5">
            <h2 className="text-xl font-semibold text-white mb-4">Kampanjer</h2>
            <CampaignManager
              accountId={currentAccount.id}
              accountHandle={currentAccount.handle}
            />
          </div>
        )}

        <button
          onClick={handleLogout}
          className="w-full text-center text-gray-500 hover:text-white text-sm py-4 transition-colors"
        >
          Logg ut
        </button>
      </div>
    </div>
  );
};
